"use client";
import React, { useEffect, useRef, useState, useCallback } from "react";
import { Images, Icons } from "@assets/assets";
import Image, { StaticImageData } from "next/image";

export type BenefitItem = {
  icon: string | StaticImageData;
  image?: string | StaticImageData;
  title: string[] | string;
  description: string;
  link?: string | undefined;
};

export type HeaderConfig = {
  title: React.ReactNode;
  subtitle: string;
  description: string;
  buttonText?: string;
  mode?: "dark" | "light" | string;
};

export type IndustryBenifitsProps = {
  id?: string;
  header: HeaderConfig;
  benefits: BenefitItem[];
  ctaButton: React.ReactNode;
};

const BenefitCard = ({
  item,
  index,
  cardRef,
  onClick,
  isExpanded,
  onToggle,
}: {
  item: BenefitItem;
  index: number;
  // eslint-disable-next-line no-unused-vars
  cardRef: (_el: HTMLElement | null) => void;
  onClick: () => void;
  isExpanded: boolean;
  // eslint-disable-next-line no-unused-vars
  onToggle: (_e: React.MouseEvent) => void;
}) => {
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const [showReadMore, setShowReadMore] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (descriptionRef.current) {
      const hasOverflow = descriptionRef.current.scrollHeight > 100;
      setShowReadMore(hasOverflow);
    }
  }, [item.description]);

  return (
    <article
      ref={cardRef}
      onClick={onClick}
      className="flex-[0_0_100%] sm:flex-[0_0_100%] lg:flex-[0_0_50%] xl:flex-[0_0_24%] 2xl:flex-[0_0_24%] text-white/90 lg:cursor-pointer group relative transition-[opacity,transform] duration-500 flex flex-col h-auto px-5 lg:px-0"
    >
      <div className="flex flex-col gap-5 shrink-0">
        <div className="text-white/80 text-[18px] clash-font tracking-widest">
          {String(index + 1).padStart(2, "0")}
        </div>
        <div className="h-px w-full bg-white/10"></div>
      </div>

      <div className="transition-[opacity,transform] duration-500 ease-in-out h-[80px] mt-8 opacity-100 overflow-hidden shrink-0">
        {mounted && (
          <Image
            src={item.icon}
            alt={
              Array.isArray(item.title)
                ? item.title.join(" ")
                : item.title || ""
            }
            width={60}
            height={60}
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        )}
      </div>

      <h3 className="mt-8 sm:mt-[100px] clash-font text-white text-[length:var(--space-20-32)] font-[500] leading-tight transition-colors duration-500 shrink-0">
        {/* {(() => {
          const fullTitle = Array.isArray(item.title)
            ? item.title.join(" ")
            : item.title || "";
          const words = fullTitle.trim().split(/\s+/);
          if (words.length <= 1) return fullTitle;
          const lastWord = words.pop();
          const remainingText = words.join(" ");
          return (
            <>
              {remainingText}
        
              {lastWord}
            </>
          );
        })()} */}
        {item.title}
      </h3>

      <div className="relative mt-5 shrink-0 md:min-h-[170px]">
        <p
          ref={descriptionRef}
          className={`text-white/70 leading-relaxed text-[length:var(--primarybtn)] transition-[opacity,color] duration-300 ${
            !isExpanded ? "line-clamp-4" : ""
          }`}
        >
          {item.description}
        </p>
        {showReadMore && (
          <button
            onClick={onToggle}
            aria-label={isExpanded ? "Read less" : "Read more"}
            className="mt-2 inline-flex items-center justify-center p-1 rounded-md text-[#225bd4] hover:bg-[#225bd4] hover:text-white transition-colors duration-300"
          >
            {isExpanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m18 15-6-6-6 6" />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            )}
          </button>
        )}
      </div>
    </article>
  );
};

export default function IndustryBenifits({
  id = "industry-benifits-food-delivery",
  header,
  benefits,
  ctaButton,
}: IndustryBenifitsProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const cardRefs = useRef<(HTMLElement | null)[]>([]);
  const [progress, setProgress] = useState(0);
  const [activeIndex, setActiveIndex] = useState(benefits.length);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // 1. Triple-cloning for extra smooth coverage
  const infiniteBenefits = [...benefits, ...benefits, ...benefits];

  const scrollToCard = useCallback((index: number, smooth = true) => {
    const track = trackRef.current;
    const card = cardRefs.current[index];
    if (!track || !card) return;

    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    const targetScroll = track.scrollLeft + (cardRect.left - trackRect.left);

    track.scrollTo({
      left: targetScroll,
      behavior: smooth ? "smooth" : "instant",
    });

    setActiveIndex(index);
  }, []);

  useEffect(() => {
    if (trackRef.current) {
      scrollToCard(benefits.length, false);
    }
  }, [benefits.length, scrollToCard]);

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const maxScroll = scrollWidth - clientWidth;

    // 1. Calculate which card is currently "active" based on scroll position
    const cardWidth = cardRefs.current[0]?.offsetWidth || 0;
    const gap = 64; // matches your 'gap-16' (16 * 4px)
    const totalCardWidth = cardWidth + gap;

    // Determine the index by dividing scroll position by card width
    const currentIndex = Math.round(scrollLeft / totalCardWidth);

    // 2. Update Progress: Map the current index to a 0-1 range based on the original list length
    // We use modulo to ensure it resets when we jump between clones
    const normalizedIndex = currentIndex % benefits.length;
    const newProgress = (normalizedIndex + 1) / benefits.length;
    setProgress(newProgress);

    // 3. Seamless Jump Logic (Existing)
    if (scrollLeft <= 5) {
      scrollToCard(currentIndex + benefits.length, false);
    } else if (scrollLeft >= maxScroll - 5) {
      scrollToCard(currentIndex - benefits.length, false);
    }
  };

  // Logic to prevent index drifting too far out of bounds
  const handleNext = () => {
    const nextIndex = activeIndex + 1;
    scrollToCard(nextIndex);
  };

  const handlePrev = () => {
    const prevIndex = activeIndex - 1;
    scrollToCard(prevIndex);
  };

  return (
    <section
      id={id}
      className="w-full max-w-[100vw] relative overflow-hidden bg-[#000000]"
    >
      <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
        <div className="absolute inset-0 z-0">
          <Image
            src={Images?.texture}
            alt="texture"
            fill
            className="object-cover mix-blend-overlay opacity-60"
            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
          />
        </div>
      </div>
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute left-0 bottom-0 -translate-x-1/3 translate-y-1/3" />

      <div className="max-w-[2500px] mx-auto w-[calc(100%_-_10%)] pt-[30px] lg:pt-[150px] pb-[10px] lg:pb-[50px] relative z-1">
        <div className="technology-header gap-[15px] w-full lg:w-auto">
          <p className="self-stretch justify-center text-[#636363] text-base mb-4 font-medium uppercase tracking-tight lg:pb-0 pb-4">
            {header.subtitle}
          </p>
          <div className="technology-header-left justify-between w-full flex flex-col lg:flex-row lg:w-auto leading-[110%]">
            <div className="flex flex-col gap-[15px]">
              <h2 className="self-stretch justify-start text-[length:var(--section-title)] font-medium clash-font min-w-[14ch]">
                <span className="text-[#FFF] capitalize leading-[110%]">
                  {header.title}
                </span>
              </h2>
            </div>
            <div className="w-full max-w-[50ch] justify-start text-[#949596] text-[length:var(--hero-para)] font-normal leading-[150%]">
              {header.description}
              <div className="technology-header-right mt-5 flex justify-start">
                {ctaButton ? ctaButton : ""}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="relative">
        {/* Navigation arrows */}
        <div className="max-w-[5000px] mx-auto w-[calc(100%_-_10%)] mt-6 sm:mt-8">
          <div className="flex gap-2 sm:gap-[10px] w-max ml-auto">
            <button
              onClick={handlePrev}
              aria-label="Previous slide"
              className="cursor-pointer transition-opacity duration-300 hover:opacity-70"
            >
              <Image
                src={Icons?.slideLeft}
                alt="Prev"
                width={40}
                height={40}
                className="sm:w-[40px] sm:h-[40px]"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </button>

            <button
              onClick={handleNext}
              aria-label="Next slide"
              className="cursor-pointer transition-opacity duration-300 hover:opacity-70"
            >
              <Image
                src={Icons?.slideRight}
                alt="Next"
                width={40}
                height={40}
                className=" sm:w-[40px] sm:h-[40px]"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </button>
          </div>
        </div>

        {/* Slider */}
        <div className="overflow-hidden pl-[4%] sm:pl-[5%] 2xl:pl-[max(5%,calc(50vw-1250px))] pr-[4%] sm:pr-[5%]">
          <div
            ref={trackRef}
            onScroll={handleScroll}
            className="flex gap-6 sm:gap-10 lg:gap-16 overflow-x-auto scroll-smooth noscroll py-4 sm:py-6"
          >
            {infiniteBenefits?.map((item, index) => (
              <BenefitCard
                key={`${index}-${item.title[0]}`}
                item={item}
                index={index % benefits.length}
                isExpanded={expandedIndex === index}
                onToggle={(e) => {
                  e.stopPropagation();
                  setExpandedIndex(expandedIndex === index ? null : index);
                }}
                cardRef={(el) => {
                  cardRefs.current[index] = el;
                }}
                onClick={() => scrollToCard(index)}
              />
            ))}
          </div>
        </div>

        {/* Progress bar */}
        <div className="max-w-[2500px] mx-auto w-[calc(100%_-_9%)] pb-10 lg:pb-[100px]">
          <div className="mt-6 sm:mt-8 h-2 sm:h-3 bg-white/5 w-full sm:w-[40%] md:w-[25%] lg:w-[20%] relative overflow-hidden rounded-full">
            <div
              className="h-full bg-[#225bd4] transition-[width] duration-300 ease-out"
              style={{ width: `${Math.max(4, progress * 100)}%` }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}
